(ns common.completion
  "Wire contract for backend -> frontend *completion messages*: one-shot
   notifications that a piece of asynchronous backend work has finished, so
   the UI can wait for it instead of racing it.

   Shared cljc on purpose. The pre-existing `:sync/sync-core-entities` push
   carries a bare \"Must match backend payload\" comment on the frontend and
   nothing enforces it - one side renaming a key is a silent break. Both the
   pusher (`backend.completion`) and the handler (`ui.completion.event`) read
   the event id, the kinds and the statuses from here, so they cannot drift.

   Not a subscription: `backend.domain.updates` already covers feeds that
   stream over time, with per-stream authorization, a compiled feed and an
   initial snapshot. A completion is a single fire-and-forget ping keyed by an
   id the initiating request already knows, so it needs none of that."
  (:require
   [malli.core :as m]))

(def event-id
  "The sente event id every completion message is pushed under.

   One id for all kinds rather than one per consumer: the frontend registers a
   single handler and routes on `:completion/kind`, so a new kind needs no new
   handler registration and no new `comms.sente` wiring."
  :completion/done)

(def search-index-kind
  "Both OpenSearch indexes (dedicated and universal) have been brought up to
   date for the subject's entity-ids - see `backend.search.utils/index-changes-now!`."
  :completion.kind/search-index)

(def all-kinds
  #{search-index-kind})

(def ok-status
  "The work ran to completion with no failures reported."
  :completion.status/ok)

(def partial-status
  "The work finished, but some part of it failed and was swallowed.

   Exists because that is the honest description of the search-indexing path:
   `index-changes!` catches per-entity-type failures and logs them rather than
   aborting the whole pass, so \"finished\" and \"every document written\" are
   genuinely different outcomes. Waiters treat this like `ok-status` - the pass
   is over, there is nothing further to wait for - but it is reported
   separately so a failure is not silently indistinguishable from success."
  :completion.status/partial)

(def error-status
  "The work threw and did not finish."
  :completion.status/error)

(def all-statuses
  #{ok-status
    partial-status
    error-status})

(def terminal-statuses
  "Every status is terminal. Spelled out so a waiter reads as \"resolve on a
   terminal status\" rather than \"resolve on anything\", and so adding a
   genuinely non-terminal status later (a progress ping, say) is a visible
   change here instead of a silent behaviour change in the waiter."
  all-statuses)

(def Subject
  "Informational description of *what* the work was about.

   Not the correlation key - `:completion/id` is. This exists so a completion
   is legible in logs and in re-frame-10x without cross-referencing a uuid,
   and so a future consumer can match on the subject if it ever needs to."
  [:map
   [:entity-ids {:optional true} [:sequential int?]]
   [:tenant-id {:optional true} int?]])

(def Completion
  [:map {:closed true}
   [:completion/id string?]
   [:completion/kind (into [:enum] (sort all-kinds))]
   [:completion/status (into [:enum] (sort all-statuses))]
   [:completion/subject {:optional true} Subject]
   [:completion/error {:optional true} string?]
   [:completion/at int?]])

(defn valid-completion?
  [payload]
  (m/validate Completion payload))

(defn terminal?
  [payload]
  (contains? terminal-statuses (:completion/status payload)))
